import { type HTMLAttributes } from 'react';
import { cn } from '../../utils/cn';

/**
 * Promotion lifecycle status — mirrors `PromotionStatus` from `@bravemove/types/promotion`.
 * Kept local — `@bravemove/types` is NOT a dep of `@bravemove/ui-web`. Spec source:
 * [`docs/design/screens/F40-components.jsx`](../../../../../docs/design/screens/F40-components.jsx)
 * `promoStatusCfg` (line 9).
 */
export type PromoStatus =
  | 'draft'
  | 'scheduled'
  | 'active'
  | 'paused'
  | 'expired'
  | 'cancelled'
  | 'suspended_by_admin';
export type PromoStatusBadgeSize = 'sm' | 'md';

export interface PromoStatusBadgeProps extends Omit<HTMLAttributes<HTMLSpanElement>, 'children'> {
  readonly status: PromoStatus;
  readonly size?: PromoStatusBadgeSize;
  /** Override the default status label. */
  readonly label?: string;
}

interface PromoStatusStyle {
  readonly box: string;
  readonly dot: string;
  readonly defaultLabel: string;
  /** `active` pulses (motion-safe) to read as live. */
  readonly pulse: boolean;
}

const STATUS_STYLE: Record<PromoStatus, PromoStatusStyle> = {
  draft: {
    box: 'bg-surface-2 text-ink-3 border border-hairline-2',
    dot: 'bg-ink-3',
    defaultLabel: 'Draft',
    pulse: false,
  },
  scheduled: {
    box: 'bg-info/15 text-ink border border-info/40',
    dot: 'bg-info',
    defaultLabel: 'Scheduled',
    pulse: false,
  },
  active: {
    box: 'bg-success/15 text-ink border border-success/40',
    dot: 'bg-success',
    defaultLabel: 'Active',
    pulse: true,
  },
  paused: {
    box: 'bg-warning/15 text-ink border border-warning/40',
    dot: 'bg-warning',
    defaultLabel: 'Paused',
    pulse: false,
  },
  expired: {
    box: 'bg-surface-2 text-ink-3 border border-hairline-2',
    dot: 'bg-ink-3',
    defaultLabel: 'Expired',
    pulse: false,
  },
  cancelled: {
    box: 'bg-surface-2 text-ink-3 border border-hairline-2',
    dot: 'bg-ink-3',
    defaultLabel: 'Cancelled',
    pulse: false,
  },
  suspended_by_admin: {
    box: 'bg-danger/15 text-ink border border-dashed border-danger',
    dot: 'bg-danger',
    defaultLabel: 'Suspended',
    pulse: false,
  },
};

const SIZE: Record<PromoStatusBadgeSize, { box: string; dot: string }> = {
  sm: { box: 'px-[7px] py-[2px] gap-1.5 text-[9px]', dot: 'size-[5px]' },
  md: { box: 'px-[9px] py-[4px] gap-1.5 text-[10px]', dot: 'size-[6px]' },
};

/**
 * F40 — `PromoStatusBadge` (shared primitive). Promotion lifecycle pill across
 * the venue promo dashboard + admin moderation queue. `active` pulses;
 * `suspended_by_admin` carries a dashed border.
 *
 * @example
 *   <PromoStatusBadge status="active" />
 *   <PromoStatusBadge status="suspended_by_admin" size="sm" />
 */
export function PromoStatusBadge({
  status,
  size = 'md',
  label,
  className,
  ...rest
}: PromoStatusBadgeProps) {
  const style = STATUS_STYLE[status];
  const sz = SIZE[size];
  const text = label ?? style.defaultLabel;
  return (
    <span
      role="img"
      aria-label={`Promotion status: ${text}`}
      data-testid={`promo-status-badge-${status}`}
      className={cn(
        'inline-flex items-center rounded uppercase whitespace-nowrap align-middle font-mono font-extrabold tracking-[0.1em]',
        style.box,
        sz.box,
        className,
      )}
      {...rest}
    >
      <span
        aria-hidden="true"
        className={cn(
          'inline-block rounded-full shrink-0',
          sz.dot,
          style.dot,
          style.pulse && 'motion-safe:animate-pulse',
        )}
      />
      <span>{text}</span>
    </span>
  );
}
